import { request } from '../../request';
import { Dayjs } from 'dayjs';
import { ShiftList } from '@libs/models/shifts/shift';
import { TIMEZONE_FALLBACK } from '@libs/config';
import { filterToQueryParams } from '../../utils';
import { Filter } from '@libs/store/filters';

const FallbackTimezone = TIMEZONE_FALLBACK;

type getEventsParams = {
  startDate?: Dayjs;
  endDate?: Dayjs;
  timezone: string;
  filters?: Record<string, Filter>;
};
export async function getEvents(params: getEventsParams): Promise<ShiftList[]> {
  const { timezone, startDate, endDate, filters = {} } = params;
  const applyFilters = filterToQueryParams(filters, {
    removeEmpty: true,
    renameMap: {
      statuses: 'status',
      staffingTypes: 'staffingtypes',
      specialties: 'specialities',
    },
  });

  const response = await request.get<ShiftEventResponseItem[]>('/v3/calendar/events', {
    params: {
      ...applyFilters,
      startDate: startDate?.toServerFilterDate(),
      endDate: endDate?.toServerFilterDate(),
      timeZone: timezone || FallbackTimezone,
    },
  });
  return response.data.map((evt) => ({
    ...evt,
    externalId: evt.externalIdAsString,
    specialty: evt.speciality ? { ...evt.speciality, name: evt.speciality.title } : null,
    recurrence: evt.recurrenceType
      ? (evt.recurrenceType.toLowerCase() as 'single' | 'series' | 'rotation') || 'single'
      : 'single',
    recurrenceId: evt.shiftRecurrenceId,
    nurseQualifications: evt.staffNurseQualificationFilters,
    otherQualifications: evt.staffOtherQualificationFilters,
    timezone: evt.startDate.timeZone,
    startDate: evt.startDate.dateTime,
    endDate: evt.endDate.dateTime,
    timeZone: evt.startDate.timeZone,
    notes: evt.note ? [evt.note] : [],
    tags: evt.tags || [],
    staff: evt.staff
      ? {
          ...evt.staff,
          fullName: [evt.staff.firstName, evt.staff.lastName].filter(Boolean).join(' '),
        }
      : null,
  }));
}

type ShiftEventResponseItem = {
  id: string; // 'shift-00000000000000000000000000';
  canHasMultipleAssignee: boolean;
  externalIdAsString: string;
  staffNurseQualificationFilters: [
    {
      id: string;
      name: string;
      abbreviation: string;
      category: string;
    },
  ];
  staffOtherQualificationFilters: [
    {
      id: string;
      name: string;
      abbreviation: string;
      category: string;
    },
  ];
  staff: {
    id: string; // 'staffmember-00000000000000000000000000';
    firstName: string;
    lastName: string;
    imageURL: string;
    nurseQualifications: [
      {
        id: string;
        name: string;
        abbreviation: string;
        category: string;
      },
    ];
  };
  assignRequest: {
    status: 'Pending' | 'Approved' | 'Declined';
    staff: {
      id: string;
      imageUrl: string;
      firstName: string;
      lastName: string;
      isInternal: true;
      agencyId: string; //'agency-00000000000000000000000000';
      agencyName: string;
      orientedRecords: [
        {
          locationId: string; //'location-00000000000000000000000000';
          oriented: 'Yes' | 'No';
        },
      ];
    };
    declineReason: string;
  };
  startDate: {
    dateTime: string; // '2025-08-28T17:02:37.714Z';
    timeZone: string;
  };
  endDate: {
    dateTime: string; // '2025-08-28T17:02:37.714Z';
    timeZone: string;
  };
  department: {
    id: string; // 'departmentnode-00000000000000000000000000';
    label: string;
  };
  subDepartment: {
    id: string; // 'departmentnode-00000000000000000000000000';
    label: string;
  };
  speciality: {
    id: string; // 'speciality-00000000000000000000000000';
    title: string;
    category: string;
  };
  tags: string[];
  note: string;
  isUrgent: true;
  cancellingReason: string;
  unassignReason: string;
  chosenQualifications: [
    {
      id: string;
      name: string;
      abbreviation: string;
      category: string;
    },
  ];
  agenciesForShare: {
    id: string; //'staffingpartner-00000000000000000000000000';
    logoURL: string;
    name: string;
    tenantId: string;
  }[];
  status:
    | 'Opened'
    | 'Pending'
    | 'Assigned'
    | 'Reassigned'
    | 'Canceled'
    | 'InProgress'
    | 'Unfilled'
    | 'Deleted'
    | 'Expired'
    | 'Unassigned'
    | 'Completed';
  location: {
    id: string; // 'location-00000000000000000000000000';
    name: string;
  };
  staffingType: 'All' | 'Internal' | 'External';
  shiftRecurrenceId: string; // 'shiftrecurrence-00000000000000000000000000';
  isShared: boolean;
  isArchive: boolean;
  recurrenceType: 'Series' | 'Single' | 'Rotation';
};
